import React, { useState } from 'react'
import useSWR from 'swr'
import { useDebounce } from 'use-debounce'
import { CheckableFilter } from '../../common/CheckableFilter'
import { FilterChip } from '../../common/FilterChip'
import {
	useFilters,
	usePushUrlFilters,
	useUrlFilters,
} from '../../common/hooks'
import {
	fetchMediaResources,
	fetchTaxonomyTerms,
	formatFileSize,
} from '../../common/utils'

export function ProductResources() {
	const [search, setSearch] = useState('')
	const [debouncedSearch] = useDebounce(search, 250)

	const urlFilters = useUrlFilters()

	const { data: productTypes = [] } = useSWR(
		'product_category',
		fetchTaxonomyTerms,
	)
	const { data: projectTypes = [] } = useSWR(
		'project_types',
		fetchTaxonomyTerms,
	)

	const {
		filters: productFilters,
		addFilter: addProductFilter,
		removeFilter: removeProductFilter,
		containsFilter: containsProductFilter,
	} = useFilters(productTypes, urlFilters.product)

	const {
		filters: projectFilters,
		addFilter: addProjectFilter,
		removeFilter: removeProjectFilter,
		containsFilter: containsProjectFilter,
	} = useFilters(projectTypes, urlFilters.project)

	usePushUrlFilters(productFilters, projectFilters)

	const { isValidating, data: files = [] } = useSWR(
		[debouncedSearch, productFilters, projectFilters],
		fetchMediaResources,
	)

	function getFilterName(slug: string): string {
		const productName =
			productTypes.find(term => term.slug === slug)?.name ?? ''

		if (productName) return productName

		return projectTypes.find(term => term.slug === slug)?.name ?? ''
	}

	return (
		<section className="product-resources">
			<div className="header">
				<div className="header-content">
					<h1 className="title">Resources Library</h1>
					<div className="search-wrapper">
						<input
							className="search"
							placeholder="Search"
							type="search"
							value={search}
							onChange={e => setSearch(e.target.value)}
						/>
						<img
							src="/wp-content/themes/custom-theme/assets/images/Search.svg"
							alt="search icon"
							className="search-icon"
						/>
					</div>
				</div>
			</div>
			<div className="main">
				<h2 className="h2 filter-title">Filters</h2>
				<section className="filters">
					<h3 className="h3">Product Type</h3>
					<div className="product-filters">
						{productTypes.map(productType => (
							<CheckableFilter
								key={productType.id}
								checked={containsProductFilter(productType.slug)}
								name={productType.name}
								onChange={checked => {
									if (checked) {
										addProductFilter(productType.slug)
									} else {
										removeProductFilter(productType.slug)
									}
								}}
							/>
						))}
					</div>

					<h3 className="h3 margin-top">Project Type</h3>
					<div className="project-filters">
						{projectTypes.map(projectType => (
							<CheckableFilter
								key={projectType.id}
								checked={containsProjectFilter(projectType.slug)}
								name={projectType.name}
								onChange={checked => {
									if (checked) {
										addProjectFilter(projectType.slug)
									} else {
										removeProjectFilter(projectType.slug)
									}
								}}
							/>
						))}
					</div>
				</section>
				<div className="filter-chips">
					{productFilters.map(filterSlug => (
						<FilterChip
							key={filterSlug}
							onRemove={() => removeProductFilter(filterSlug)}
						>
							{getFilterName(filterSlug)}
						</FilterChip>
					))}
					{projectFilters.map(filterSlug => (
						<FilterChip
							key={filterSlug}
							onRemove={() => removeProjectFilter(filterSlug)}
						>
							{getFilterName(filterSlug)}
						</FilterChip>
					))}
				</div>
				<section className="files">
					<header className="order-header">
						<span className="name">Name</span>
						<span className="file-size">File Size</span>
					</header>
					{files.map(file => {
						const fileType = mimeTypeToFileType(file.mime_type)

						return (
							<div key={file.id} className="file-row">
								<img
									className="icon"
									src={
										'/wp-content/themes/custom-theme/assets/images/' +
										fileType +
										'-icon.svg'
									}
									alt={fileType}
									title={fileType}
								/>
								<span
									className="name"
									dangerouslySetInnerHTML={{ __html: file.title.rendered }}
								/>
								<span className="size">
									{formatFileSize(file.media_details.filesize)}
								</span>
								<a className="download" href={file.source_url} target="_blank">
									Download
									<img
										src="/wp-content/themes/custom-theme/assets/images/download.svg"
										alt="download"
									/>
								</a>
							</div>
						)
					})}
					{isValidating ? (
						<div className="no-results">Loading...</div>
					) : (
						<div className="no-results">
							There are no {files.length > 0 ? 'more ' : ''}results.
						</div>
					)}
				</section>
			</div>
		</section>
	)
}

type FileType = 'pdf' | 'autocad' | 'image' | 'spreadsheet' | 'default-file'

function mimeTypeToFileType(mimeType: string): FileType {
	switch (mimeType) {
		case 'application/pdf':
			return 'pdf'

		case 'image/vnd.dwg':
		case 'image/vnd.dxf':
			return 'autocad'

		case 'text/csv':
		case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
		case 'application/vnd.ms-excel':
			return 'spreadsheet'

		default:
			if (mimeType.indexOf('image/') === 0) {
				return 'image'
			}

			return 'default-file'
	}
}
